import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IArquivoOperacao } from '../../interfaces/IArquivoOperacao';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import FolhaPagamentoService from '../../services/FolhaPagamentoService';

const ACCEPTED_EXTENSIONS = '.xlsx,.xls';
const PER_PAGE = 10;

function statusInfo(arquivo: IArquivoOperacao): { label: string; color: string; bg: string } {
    if (arquivo.arquivoErro)
        return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (arquivo.arquivoProcessando)
        return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (arquivo.arquivoProcessou)
        return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    if (arquivo.arquivoIniciouProcesso)
        return { label: 'AGUARDANDO', color: '#009ef7', bg: '#009ef722' };
    return { label: 'PENDENTE', color: '#6c757d', bg: '#6c757d22' };
}

interface Props {
    esteiraId: number;
    cartaoId: number;
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

function ModalFolhaPagamento({ esteiraId, cartaoId, show, onHide, onSuccess }: Props) {
    const [arquivos, setArquivos] = useState<IArquivoOperacao[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [iniciando, setIniciando] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const buscar = useCallback(() => {
        if (!esteiraId || !cartaoId) return;
        setLoading(true);
        ArquivoOperacaoService.listarPorCartao(esteiraId, cartaoId, page, PER_PAGE)
            .then(res => {
                const arquivosFolha = res.items.filter((a: IArquivoOperacao) => a.tipo === 'FOLHA_PAGAMENTO');
                setArquivos(arquivosFolha);
                setTotal(arquivosFolha.length);
                setTotalPages(res.totalPages);
            })
            .catch(err => console.error('Erro ao buscar arquivos de folha:', err))
            .finally(() => setLoading(false));
    }, [esteiraId, cartaoId, page]);

    useEffect(() => {
        if (show && esteiraId && cartaoId) {
            buscar();
        }
    }, [show, esteiraId, cartaoId, buscar]);

    const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        let erros = 0;

        for (const file of Array.from(files)) {
            try {
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ArquivoOperacaoService.registrar(esteiraId, 'FOLHA_PAGAMENTO', generatedFileName, file.name, cartaoId);
            } catch {
                erros++;
            }
        }

        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (erros > 0) {
            Swal.fire({ text: `${erros} arquivo(s) falharam no envio.`, icon: 'warning', timer: 3000, showConfirmButton: false });
        } else {
            Swal.fire({ text: 'Arquivo(s) enviado(s) com sucesso!', icon: 'success', timer: 2000, showConfirmButton: false });
        }

        buscar();
    }, [esteiraId, cartaoId, buscar]);

    const handleIniciarProcessamento = useCallback(async () => {
        const pendentes = arquivos.filter(a => !a.arquivoProcessou && !a.arquivoErro);
        if (pendentes.length === 0) {
            Swal.fire({ text: 'Nenhum arquivo pendente para processar.', icon: 'warning', timer: 3000, showConfirmButton: false });
            return;
        }

        setIniciando(true);
        try {
            await FolhaPagamentoService.iniciarProcessamento(esteiraId, cartaoId);
            Swal.fire({ text: 'Processamento iniciado! O cartão será movido para Conciliado Parcial ao concluir, com as sugestões de conciliação já aplicadas.', icon: 'success', timer: 4000, showConfirmButton: false });
            onHide();
            onSuccess();
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || 'Erro ao iniciar processamento.', icon: 'error', timer: 4000, showConfirmButton: false });
        } finally {
            setIniciando(false);
        }
    }, [esteiraId, cartaoId, arquivos, onHide, onSuccess]);

    const handlePage = (p: number) => {
        if (p >= 1 && p <= totalPages) setPage(p);
    };

    const temPendentes = arquivos.some(a => !a.arquivoProcessou && !a.arquivoErro && !a.arquivoProcessando);

    return (
        <Modal size='lg' centered show={show} onHide={onHide}>
            <div className='modal-content' style={{ backgroundColor: '#F4F5F7' }}>
                <div className='modal-header'>
                    <h5 className='modal-title titulo-modal'>Importação de Folha de Pagamento</h5>
                    <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
                        <i onClick={onHide} className='fas fa-times' style={{ color: '#5E6397', cursor: 'pointer' }} />
                    </div>
                </div>

                <div className='modal-body'>
                    {/* Upload area */}
                    <div className='card card-body mb-4 p-3' style={{ backgroundColor: '#fff', border: '1px solid #e4e6ef' }}>
                        <div className='fw-bold mb-1' style={{ fontSize: 13 }}>Enviar arquivo de folha de pagamento</div>
                        <div className='text-muted mb-2' style={{ fontSize: 11 }}>Formatos aceitos: .xlsx, .xls (relatório de folha exportado do seu sistema)</div>
                        <div className='d-flex align-items-center gap-3'>
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept={ACCEPTED_EXTENSIONS}
                                multiple
                                className='form-control form-control-sm'
                                style={{ maxWidth: 320 }}
                                disabled={uploading || iniciando}
                                onChange={handleUpload}
                            />
                            {uploading && <span className='spinner-border spinner-border-sm text-primary' />}
                        </div>
                    </div>

                    {/* File list */}
                    {loading ? (
                        <div className='d-flex justify-content-center py-4'>
                            <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                            <span>Carregando...</span>
                        </div>
                    ) : arquivos.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d', fontSize: 13 }}>
                            Nenhum arquivo enviado ainda. Selecione o relatório de folha acima.
                        </div>
                    ) : (
                        <>
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                <span className='fw-bold' style={{ fontSize: 13 }}>Arquivos enviados</span>
                                <span className='text-muted' style={{ fontSize: 12 }}>{total} arquivo(s)</span>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mb-0'>
                                    <thead>
                                        <tr className='fw-bold text-muted' style={{ fontSize: 12 }}>
                                            <th>Nome</th>
                                            <th className='text-nowrap'>Enviado em</th>
                                            <th className='text-center'>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {arquivos.map(arquivo => {
                                            const st = statusInfo(arquivo);
                                            return (
                                                <tr key={arquivo.id}>
                                                    <td style={{ fontSize: 12, maxWidth: 260 }}>
                                                        <span title={arquivo.nomeOriginal} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {arquivo.nomeOriginal}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                        {moment(arquivo.dataCadastro).format('DD/MM/YYYY HH:mm')}
                                                    </td>
                                                    <td className='text-center'>
                                                        <span style={{
                                                            fontSize: 10,
                                                            fontWeight: 'bold',
                                                            color: st.color,
                                                            backgroundColor: st.bg,
                                                            padding: '2px 8px',
                                                            borderRadius: 4,
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className='d-flex justify-content-between align-items-center mt-2'>
                                    <span style={{ fontSize: 12, color: '#6c757d' }}>Página {page} de {totalPages}</span>
                                    <div className='d-flex gap-1'>
                                        <button className='btn btn-sm btn-light' disabled={page === 1} onClick={() => handlePage(page - 1)}>‹</button>
                                        <button className='btn btn-sm btn-light' disabled={page === totalPages} onClick={() => handlePage(page + 1)}>›</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className='modal-footer d-flex justify-content-between'>
                    <button className='btn btn-light' onClick={onHide} disabled={iniciando}>
                        Fechar
                    </button>
                    <button
                        className='btn btn-primary'
                        disabled={!temPendentes || iniciando || uploading}
                        onClick={handleIniciarProcessamento}
                    >
                        {iniciando ? (
                            <>
                                <span className='spinner-border spinner-border-sm me-2' />
                                Iniciando...
                            </>
                        ) : 'Iniciar Processamento'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default ModalFolhaPagamento;
